import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useCallback, useEffect } from 'react';
import ImageResize from 'tiptap-extension-resize-image';
import { AddCircleIcons } from '../../icons/icons';
import { Button } from '../components';
import EditorToolbar from './EditorToolbar';

interface RichTextEditorProps {
  jsonStringTemplate: string;
  validVariables?: { name: string; variable: string }[];
  saveTemplate: (template: string, jsonTemplate: string) => void;
  value?: string;
  onEditorReady?: (editor: any) => void;
  buttonText?: string;
  hideSaveButton?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  jsonStringTemplate,
  validVariables,
  saveTemplate,
  value,
  onEditorReady,
  buttonText = 'Save',
  hideSaveButton = false,
}) => {
  const VARIABLES = ['applicant_name', 'job_title_name', 'end_of_assessment'];
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Write something...' }),
      Image.configure({
        allowBase64: true,
      }),
      ImageResize,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
    ],
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none min-h-[360px] [&_ol]:list-decimal [&_ul]:list-disc rounded-lg border border-slate-200 bg-white p-3 text-slate-700 outline-none focus:ring-2 focus:ring-emerald-100',
      },
    },
    content: value ? value : JSON.parse(jsonStringTemplate),
  });

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  const handleInsertVariable = useCallback(
    (variable: string) => {
      if (editor && variable.length !== 0) {
        const variableText = VARIABLES.includes(variable)
          ? `{{${variable}}}`
          : variable;
        editor.chain().focus().insertContent(variableText).run();
      }
    },
    [editor],
  );

  const handleInsertImage = useCallback(
    (variable: string) => {
      if (editor) {
        editor.chain().focus().setImage({ src: variable }).run();
      }
    },
    [editor],
  );

  const setLink = useCallback(
    (url: string) => {
      if (editor) {
        if (url === null) {
          return;
        }

        if (url === '') {
          editor.chain().focus().extendMarkRange('link').unsetLink().run();
          return;
        }

        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: `{{${url}}}` })
          .run();
      }
    },
    [editor],
  );

  const exportToHTML = () => {
    if (editor) {
      const html = editor.getHTML();
      const htmlJson = JSON.stringify(editor.getJSON());
      if (jsonStringTemplate !== htmlJson) {
        saveTemplate(html, htmlJson);
      }
    }
  };

  const handleVariableClick = (variableName: string, variable: string) => {
    switch (variableName) {
      case 'Company Logo':
        return handleInsertImage(variable);
      case 'Assessment Link':
        return setLink(variable);
      case 'Unset Link':
        return editor && editor.chain().focus().unsetLink().run();
      default:
        return handleInsertVariable(variable);
    }
  };

  const variableDisabled = (variableName: string) => {
    if (editor) {
      switch (variableName) {
        case 'Assessment Link':
          return editor.state.selection.to - editor.state.selection.from === 0;
        case 'Unset Link':
          return !editor.isActive('link');
        default:
          return false;
      }
    } else {
      return;
    }
  };

  return (
    <div className='rounded-lg border border-slate-200 bg-white shadow-sm'>
      <EditorToolbar editor={editor} />
      {!hideSaveButton && (
        <div className='flex w-full justify-end border-t border-slate-100 px-3 py-2'>
          <Button
              variant='primary'
              label={buttonText}
              onClick={exportToHTML}
              className='w-full sm:w-auto'
          />
        </div>
      )}
      <div className='relative grid grid-cols-1 gap-3 p-3 lg:grid-cols-[minmax(0,1fr)_260px]'>
        <div className='min-w-0 order-last flex-grow lg:order-first'>
          <EditorContent editor={editor} />
        </div>
        <aside className='min-w-0 rounded-lg border border-slate-200 bg-slate-50 p-3'>
          <h3 className='mb-2 text-sm font-extrabold text-slate-900'>
            Insert Variables
          </h3>
          <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1'>
            { validVariables && (
              <div className='contents'>
                { validVariables.map((variable) => (
                  <button
                      key={variable.variable}
                      onClick={() =>
                        handleVariableClick(variable.name, variable.variable)
                      }
                      className='flex min-h-9 items-center gap-2 rounded-md border border-emerald-100 bg-white px-3 py-2 text-start text-xs font-semibold text-emerald-700 transition hover:border-emerald-200 hover:bg-emerald-50'
                  >
                    <AddCircleIcons /> <span>{ variable.name }</span>
                  </button>
                )) }
              </div>
            ) }
          </div>
        </aside>
      </div>
    </div>
  );
};

export default RichTextEditor;
